import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TrafficLight from "@/components/TrafficLight";
import CertModal from "@/components/CertModal";
import TrainingMenu from "@/components/TrainingMenu";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { toast } from "sonner";
import { Plus, Search, Users, AlertTriangle, AlertOctagon, CheckCircle2, X, Edit2, Trash2, ShieldPlus } from "lucide-react";

const STATUS_RANK = { expired: 0, expiring_soon: 1, current: 2, none: 3, unknown: 4 };

// Extract last name (last whitespace-separated token) for sorting, trimming punctuation.
const lastName = (n = "") => {
  const parts = String(n).trim().split(/\s+/);
  return (parts[parts.length - 1] || "").toLowerCase();
};

export default function Roster() {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("alpha"); // alpha | trade | exp
  // Tile-driven primary grouping: which status (if any) bubbles to the top.
  // null = no grouping (Total selected / default). Otherwise: "current" | "expiring_soon" | "expired".
  const [statusFilter, setStatusFilter] = useState(null);
  const [workTypeFilter, setWorkTypeFilter] = useState([]); // string[]; empty = no filter
  const [trades, setTrades] = useState([]);
  const [certTypes, setCertTypes] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showCert, setShowCert] = useState(null); // employee
  const [showEdit, setShowEdit] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees");
      setEmployees(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    api.get("/meta/trades").then(r => setTrades(r.data.trades || []));
    api.get("/meta/cert-types").then(r => setCertTypes(r.data.types || []));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Normalize an employee's work_type (which may be a string, an array, or absent).
    const empWorkTypes = (e) => {
      if (Array.isArray(e.work_type)) return e.work_type;
      if (typeof e.work_type === "string" && e.work_type.trim()) return [e.work_type.trim()];
      return [];
    };
    let list = employees.filter((e) =>
      !q || e.name.toLowerCase().includes(q) || e.trade.toLowerCase().includes(q) ||
      (e.email || "").toLowerCase().includes(q)
    );
    // Work-type chip filter: OR across selected chips; empty selection = no restriction.
    // Match "Other: <custom>" when the generic "Other" chip is selected.
    if (workTypeFilter.length) {
      const wanted = new Set(workTypeFilter);
      list = list.filter((e) =>
        empWorkTypes(e).some((w) => wanted.has(w) || (wanted.has("Other") && w.startsWith("Other:")))
      );
    }
    // Secondary comparator chosen by the sort buttons.
    const secondary = (a, b) => {
      if (sort === "alpha") return lastName(a.name).localeCompare(lastName(b.name)) || a.name.localeCompare(b.name);
      if (sort === "trade") return a.trade.localeCompare(b.trade) || lastName(a.name).localeCompare(lastName(b.name));
      if (sort === "exp")   return (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9);
      return 0;
    };
    // Primary grouping: tile selection (if any) pulls matching status to the top.
    if (statusFilter) {
      list = [...list].sort((a, b) => {
        const aMatch = a.status === statusFilter ? 0 : 1;
        const bMatch = b.status === statusFilter ? 0 : 1;
        return aMatch - bMatch || secondary(a, b);
      });
    } else {
      list = [...list].sort(secondary);
    }
    return list;
  }, [employees, query, sort, statusFilter, workTypeFilter]);

  const counts = useMemo(() => {
    const c = { expired: 0, expiring_soon: 0, current: 0, none: 0 };
    employees.forEach((e) => c[e.status] = (c[e.status] || 0) + 1);
    return c;
  }, [employees]);

  const canEdit = user?.role === "admin" || user?.role === "supervisor";
  const canDelete = user?.role === "admin";

  return (
    <div className="p-4 md:p-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="inline-block bg-safety-yellow text-black px-2 py-0.5 font-bold uppercase tracking-widest text-[10px] mb-3">
            Main Roster
          </div>
          <h1 className="font-heading text-4xl md:text-6xl">YOUR CREW</h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-base">
            Master list of all employees · <b className="text-foreground">{employees.length} total</b>
          </p>
        </div>
        {canEdit ? (
          <div className="flex items-center gap-3">
            <TrainingMenu />
            <button data-testid="new-employee-btn" onClick={() => setShowNew(true)} className="btn-primary">
              <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> New Employee
            </button>
          </div>
        ) : (
          <TrainingMenu />
        )}
      </div>

      {/* stat tiles — click to bring matching status to the top */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatTile
          testId="tile-total"
          label="Total"
          value={employees.length}
          Icon={Users}
          accent="bg-foreground text-background"
          active={statusFilter === null}
          onClick={() => setStatusFilter(null)}
        />
        <StatTile
          testId="tile-current"
          label="Current"
          value={counts.current || 0}
          Icon={CheckCircle2}
          accent="bg-status-current text-white"
          active={statusFilter === "current"}
          onClick={() => setStatusFilter(statusFilter === "current" ? null : "current")}
        />
        <StatTile
          testId="tile-expiring"
          label="Expiring"
          value={counts.expiring_soon || 0}
          Icon={AlertTriangle}
          accent="bg-status-expiring text-black"
          active={statusFilter === "expiring_soon"}
          onClick={() => setStatusFilter(statusFilter === "expiring_soon" ? null : "expiring_soon")}
        />
        <StatTile
          testId="tile-expired"
          label="Expired"
          value={counts.expired || 0}
          Icon={AlertOctagon}
          accent="bg-status-expired text-white"
          active={statusFilter === "expired"}
          onClick={() => setStatusFilter(statusFilter === "expired" ? null : "expired")}
        />
      </div>

      {/* controls */}
      <div className="brutal-card p-4 md:p-5 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="roster-search"
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, trade, email..."
            className="industrial-input pl-11"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { k: "alpha", label: "Last Name" },
            { k: "trade", label: "By Trade" },
            { k: "exp", label: "By Expiration" },
          ].map((s) => (
            <button key={s.k} data-testid={`sort-${s.k}`}
              onClick={() => setSort(s.k)}
              className={`h-14 px-4 border-2 font-bold uppercase tracking-wider text-xs transition-colors ${
                sort === s.k
                  ? "bg-safety-yellow text-black border-black dark:border-safety-yellow"
                  : "bg-transparent border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black"
              }`}>{s.label}</button>
          ))}
        </div>
      </div>

      {/* work-type filter chips */}
      <WorkTypeFilter selected={workTypeFilter} onChange={setWorkTypeFilter} />

      {/* list */}
      {loading ? (
        <div className="p-10 text-center text-muted-foreground font-bold uppercase tracking-wider">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="p-10 text-center brutal-card">
          <div className="font-heading text-2xl">No employees found</div>
          <div className="text-muted-foreground text-sm mt-2">Add your first crew member or adjust your search.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="roster-list">
          {filtered.map((emp) => (
            <EmployeeRow
              key={emp.id} emp={emp}
              canEdit={canEdit} canDelete={canDelete}
              onAddCert={() => setShowCert(emp)}
              onEdit={() => setShowEdit(emp)}
              onDelete={async () => {
                if (!window.confirm(`Delete ${emp.name}?`)) return;
                try { await api.delete(`/employees/${emp.id}`); toast.success("Deleted"); load(); }
                catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
            />
          ))}
        </div>
      )}

      {showNew && (
        <EmployeeModal trades={trades} onClose={() => setShowNew(false)} onSave={async (payload) => {
          try { await api.post("/employees", payload); toast.success("Employee created"); setShowNew(false); load(); }
          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }} />
      )}

      {showEdit && (
        <EmployeeModal initial={showEdit} trades={trades} onClose={() => setShowEdit(null)} onSave={async (payload) => {
          try { await api.patch(`/employees/${showEdit.id}`, payload); toast.success("Updated"); setShowEdit(null); load(); }
          catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        }} />
      )}

      {showCert && (
        <CertModal employee={showCert} certTypes={certTypes} userRole={user?.role}
          onClose={() => setShowCert(null)} onRefresh={load} />
      )}
    </div>
  );
}

function StatTile({ label, value, Icon, accent, active, onClick, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-pressed={!!active}
      className={`brutal-card p-5 flex items-center gap-4 text-left transition-all duration-150 cursor-pointer ${
        active
          ? "ring-4 ring-safety-yellow ring-offset-2 ring-offset-background -translate-x-[2px] -translate-y-[2px] shadow-brutalYellow"
          : "hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-brutalYellow"
      }`}
    >
      <div className={`h-14 w-14 flex items-center justify-center border-2 border-black ${accent}`}>
        <Icon className="w-6 h-6" strokeWidth={3} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
          {label}
          {active && (
            <span className="bg-safety-yellow text-black px-1.5 py-0.5 text-[8px] font-black tracking-widest">
              SORTING
            </span>
          )}
        </div>
        <div className="font-heading text-4xl leading-none mt-1">{value}</div>
      </div>
    </button>
  );
}

function EmployeeRow({ emp, canEdit, canDelete, onAddCert, onEdit, onDelete }) {
  // Whole-row click triggers the same navigation as the CERT button.
  const handleRowClick = () => onAddCert();
  const stop = (fn) => (e) => { e.stopPropagation(); fn(e); };
  const onRowKey = (e) => {
    if (e.target !== e.currentTarget) return; // ignore key events bubbling from inner buttons
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleRowClick(); }
  };

  return (
    <div
      className="brutal-card p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-3 md:gap-5"
      data-testid={`emp-row-${emp.id}`}
      role="button"
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={onRowKey}
    >
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="h-12 w-12 flex items-center justify-center bg-safety-yellow text-black font-heading text-xl border-2 border-black">
          {emp.name.split(" ").map(s => s[0]).slice(0, 2).join("")}
        </div>
        <div className="min-w-0">
          <div className="font-heading text-xl truncate">{emp.name}</div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {emp.trade}{(() => {
              const list = Array.isArray(emp.work_type)
                ? emp.work_type
                : (typeof emp.work_type === "string" && emp.work_type ? [emp.work_type] : []);
              // Prefer the custom description over the bare "Other" label.
              const pretty = list.map((w) => (w.startsWith("Other: ") ? w.slice(7) : w));
              return pretty.length ? ` · ${pretty.join(" / ")}` : "";
            })()}{emp.email ? ` · ${emp.email}` : ""}
          </div>
          {emp.certifications?.length ? (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {emp.certifications.slice(0, 3).map((c) => (
                <span key={c.id} className="chip bg-muted text-foreground border-foreground/40 dark:border-safety-yellow/40">
                  {c.cert_type}
                </span>
              ))}
              {emp.certifications.length > 3 && (
                <span className="chip bg-muted text-muted-foreground">+{emp.certifications.length - 3}</span>
              )}
            </div>
          ) : (
            <div className="text-xs mt-2 text-muted-foreground">No certifications</div>
          )}
        </div>
      </div>

      <TrafficLight status={emp.status} size="md" testId={`status-${emp.id}`} />

      {canEdit && (
        <div className="flex gap-2">
          <button data-testid={`add-cert-${emp.id}`} onClick={stop(onAddCert)}
            className="h-12 px-3 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black transition-colors flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
            <ShieldPlus className="w-4 h-4" strokeWidth={2.5} /> Cert
          </button>
          <button data-testid={`edit-emp-${emp.id}`} onClick={stop(onEdit)}
            className="h-12 w-12 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black transition-colors flex items-center justify-center">
            <Edit2 className="w-4 h-4" strokeWidth={2.5} />
          </button>
          {canDelete && (
            <button data-testid={`del-emp-${emp.id}`} onClick={stop(onDelete)}
              className="h-12 w-12 border-2 border-status-expired text-status-expired hover:bg-status-expired hover:text-white transition-colors flex items-center justify-center">
              <Trash2 className="w-4 h-4" strokeWidth={2.5} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  useBodyScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}>
      <div className="bg-background brutal-card w-full md:max-w-xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b-2 border-foreground dark:border-safety-yellow p-4 md:p-5 sticky top-0 bg-background">
          <div className="font-heading text-2xl">{title}</div>
          <button onClick={onClose} data-testid="modal-close" className="h-10 w-10 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black">
            <X className="w-5 h-5 mx-auto" strokeWidth={3} />
          </button>
        </div>
        <div className="p-5 md:p-6">{children}</div>
      </div>
    </div>
  );
}

function EmployeeModal({ initial, trades, onClose, onSave }) {
  const WORK_TYPES = ["Commercial", "Residential", "Service", "Shop", "Warehouse", "Parts", "Sales", "Trucking", "Other"];
  const OTHER_MAX = 15;
  // Normalize any legacy single-string work_type into an array for the multi-select UI.
  const normalizeWorkTypes = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === "string" && v.trim()) return [v.trim()];
    return [];
  };
  // Separate the "Other: <desc>" value from the plain chip state so the UI
  // can show a short free-form input while keeping storage as a single array.
  const splitOther = (list) => {
    const base = [];
    let desc = "";
    for (const w of list) {
      if (w === "Other" || w.startsWith("Other:")) {
        if (!base.includes("Other")) base.push("Other");
        const after = w.startsWith("Other:") ? w.slice(6).trim() : "";
        if (after) desc = after;
      } else {
        base.push(w);
      }
    }
    return { base, desc };
  };
  const [form, setForm] = useState(() => {
    const base = initial || { name: "", email: "", trade: trades[0] || "General", phone: "", work_type: [] };
    const list = normalizeWorkTypes(base.work_type);
    const split = splitOther(list);
    return { ...base, work_type: split.base, _otherDesc: split.desc };
  });
  const otherSelected = (form.work_type || []).includes("Other");
  const toggleWorkType = (t) => {
    setForm((f) => {
      const set = new Set(f.work_type || []);
      if (set.has(t)) set.delete(t); else set.add(t);
      // Clear the free-form description when Other is deselected.
      const next = { ...f, work_type: Array.from(set) };
      if (t === "Other" && !set.has("Other")) next._otherDesc = "";
      return next;
    });
  };
  const submit = (e) => {
    e.preventDefault();
    // Rebuild work_type: if Other is selected and a description was typed,
    // replace the plain "Other" with "Other: <desc>".
    let list = (form.work_type || []).filter((w) => w !== "Other");
    if (otherSelected) {
      const desc = (form._otherDesc || "").trim().slice(0, OTHER_MAX);
      list.push(desc ? `Other: ${desc}` : "Other");
    }
    const payload = {
      name: form.name.trim(),
      trade: form.trade,
      email: form.email && form.email.trim() ? form.email.trim() : null,
      phone: form.phone && form.phone.trim() ? form.phone.trim() : null,
      work_type: list.length ? list : null,
    };
    if (typeof form.active === "boolean") payload.active = form.active;
    onSave(payload);
  };
  return (
    <Modal title={initial ? "Edit Employee" : "New Employee"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Name</label>
          <input data-testid="emp-name" required className="industrial-input" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Trade</label>
          <select data-testid="emp-trade" required className="industrial-input" value={form.trade}
            onChange={(e) => setForm({ ...form, trade: e.target.value })}>
            {trades.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div data-testid="emp-work-type">
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">
            Work Type <span className="text-muted-foreground/70 normal-case tracking-normal">(select any that apply)</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {WORK_TYPES.map((t) => {
              const selected = (form.work_type || []).includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  data-testid={`emp-work-type-${t.toLowerCase()}`}
                  aria-pressed={selected}
                  onClick={() => toggleWorkType(t)}
                  className={`px-3 py-2 text-xs font-black uppercase tracking-widest border-2 border-black transition-colors ${
                    selected ? "bg-safety-yellow text-black" : "bg-muted text-foreground hover:bg-safety-yellow/20"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          {otherSelected && (
            <div className="mt-3">
              <label className="block text-[11px] font-bold uppercase tracking-wider mb-1.5 text-muted-foreground">
                Describe "Other" (optional, {OTHER_MAX} char max)
              </label>
              <input
                data-testid="emp-work-type-other-desc"
                maxLength={OTHER_MAX}
                value={form._otherDesc || ""}
                onChange={(e) => setForm({ ...form, _otherDesc: e.target.value })}
                placeholder="e.g., Night Shift"
                className="industrial-input"
              />
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                {(form._otherDesc || "").length}/{OTHER_MAX}
              </div>
            </div>
          )}
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Email (optional)</label>
          <input data-testid="emp-email" type="email" className="industrial-input" value={form.email || ""}
            onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-1.5">Phone (optional)</label>
          <input data-testid="emp-phone" className="industrial-input" value={form.phone || ""}
            onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <button type="submit" data-testid="emp-save" className="btn-primary w-full">
          {initial ? "Save Changes" : "Create Employee"}
        </button>
      </form>
    </Modal>
  );
}


const WORK_TYPES_ALL = ["Commercial", "Residential", "Service", "Shop", "Warehouse", "Parts", "Sales", "Trucking", "Other"];

function WorkTypeFilter({ selected, onChange }) {
  const toggle = (t) => {
    const set = new Set(selected);
    if (set.has(t)) set.delete(t); else set.add(t);
    onChange(Array.from(set));
  };
  const hasAny = selected.length > 0;
  return (
    <div className="brutal-card p-4 md:p-5 mb-6" data-testid="work-type-filter">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Filter by Work Type {hasAny && <span className="text-foreground">· {selected.length} selected</span>}
        </div>
        {hasAny && (
          <button
            type="button"
            data-testid="work-type-filter-clear"
            onClick={() => onChange([])}
            className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" strokeWidth={3} /> Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {WORK_TYPES_ALL.map((t) => {
          const on = selected.includes(t);
          return (
            <button
              key={t}
              type="button"
              data-testid={`work-type-chip-${t.toLowerCase()}`}
              aria-pressed={on}
              onClick={() => toggle(t)}
              className={`px-3 py-2 text-xs font-black uppercase tracking-widest border-2 border-black transition-colors ${
                on ? "bg-safety-yellow text-black" : "bg-muted text-foreground hover:bg-safety-yellow/20"
              }`}
            >
              {t}
            </button>
          );
        })}
      </div>
    </div>
  );
}
